import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar, Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, shape, string,
} from 'prop-types'

import { getFeatureFilter } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { FEATURES } from './constants'

function FeaturesContent({ features = FEATURES, hideReset = false }) {
  const translate = useTranslate()

  // Selecting a joined string keeps zustand's strict-equality happy while the list of
  // subscribed slices lives in each feature's `select`, not hardcoded per asset type.
  const selectedKey = useSearchPageStore(useCallback(
    state => features.filter(feature => feature.select(state)).map(feature => feature.value).join(','),
    [features],
  ))
  const selectedFeatures = useMemo(() => (selectedKey ? selectedKey.split(',') : []), [selectedKey])
  const trackFilterSelect = useTrackFilterSelect()

  const translatedFeatures = useMemo(() => features.map(({ value, labelKey }) => ({
    value,
    label: translate(`search_page.filters.${labelKey}`),
  })), [features, translate])

  // Single group, no sub-heading: the collapsible header already names the section "Features".
  const featuresConfig = useMemo(() => [{
    label: '',
    filters: translatedFeatures,
  }], [translatedFeatures])

  const handleChange = useCallback((_, value, isOn) => {
    const feature = features.find(item => item.value === value)
    if (feature && isOn) {
      const { name, value: filterValue } = getFeatureFilter(feature.value)
      trackFilterSelect(name, filterValue)
    }
    feature?.onChange(isOn)
  }, [features, trackFilterSelect])

  // Delegating to each feature's own onChange keeps the low-poly nuance in the constants:
  // its off-toggle removes only its polygon range, so other Poly count selections survive.
  const handleReset = useCallback(() => {
    features.forEach(feature => feature.onChange(false))
  }, [features])

  const hasSelectedAnyFilters = selectedFeatures.length > 0

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={featuresConfig}
        selectedFilters={selectedFeatures}
        onFiltersChange={handleChange}
        limit={0}
        isControlled
        useNewCheckboxes
        hideShowMore
        hideLastDivider
        baseFormWrapperClassName="filter-wrapper__content"
      />
      {!hideReset && (
        <Button
          type="ghost-primary"
          size="small"
          customClass="filter-wrapper__reset-button"
          onClick={handleReset}
          disabled={!hasSelectedAnyFilters}
        >
          {translate('search_page.filters.features.reset')}
        </Button>
      )}
    </>
  )
}

FeaturesContent.propTypes = {
  features: arrayOf(shape({
    value: string.isRequired,
    labelKey: string.isRequired,
    select: func.isRequired,
    onChange: func.isRequired,
  })),
  hideReset: bool,
}

export default memo(FeaturesContent)
